<script setup>
import { ref, getCurrentInstance, computed } from 'vue'
import { DriverStore } from '../pinia/Driver.js'
import SKliveryCard from '../components/skeletons/SKliveryCard.vue'

const { proxy } = getCurrentInstance()
const liveries = ref([])  // All liveries from API
const carList = ref([])  // List of cars fetched from the API
const carfiltre = ref('')  // Selected car for filtering
const sortOrder = ref('desc')  // Sort order, default is descending
const selectedImage = ref(null)  // Stocke l'image sélectionnée pour la popup
const isImagePopupOpen = ref(false)  // Contrôle l'ouverture de la popup
const dialogWidth = ref('auto'); // Largeur dynamique du dialogue
const dialogHeight = ref('auto'); // Hauteur dynamique du dialogue
const imageStyles = ref({});
const isEditMode = ref(false)
const driver = DriverStore()
const isLoading = ref(true)  // Loading state

// Fetch existing liveries
async function getLiveries() {
  isLoading.value = true
  const [res] = await Promise.all([
    proxy.$api({ serviceName: "liveries", methodName: "getLiveries" }),
    new Promise(resolve => setTimeout(resolve, 800)) // Minimum 800ms loading
  ])
  liveries.value = res
  isLoading.value = false
}

const selectedLivery = ref({ imageUrl: '', title: '', code: '' }); // Store livery data for the popup

function openImagePopup(livery) {
  const img = new Image();
  img.onload = () => {
    const imgWidth = img.width;
    const imgHeight = img.height;

    // Set dialog size dynamically based on image dimensions
    dialogWidth.value = imgWidth + 'px';
    dialogHeight.value = imgHeight + 'px';

    // Open the popup with livery details
    selectedLivery.value = {
      imageUrl: 'https://cdn.nightriderz.world/images/website/liveries/' + livery.code + '.png',
      title: livery.liveryname,
      code: livery.code,
      author: livery.personaName,
      car: livery.carFullName
    };
    isImagePopupOpen.value = true;
  };
  img.src = 'https://cdn.nightriderz.world/images/website/liveries/' + livery.code + '.png';
}


// Fetch the list of cars for filtering
getCarClasses()
async function getCarClasses() {
  const res = await proxy.$api({ serviceName: "liveries", methodName: "getCarClasses" })
  if (res && Array.isArray(res)) {
    carList.value = res;  // No need to map, API already returns the correct structure
  }
}

const isFormOpen = ref(false)
const isHowToOpen = ref(false)  // State for the "how to" popup
const form = ref({
  liveryName: '',
  liveryCode: '',
  liveryImage: null, // The selected file will be stored here
})

const previewImage = ref(null) // Stores the image URL for preview
const errorMessage = ref('')

function publishLivery() {
  resetForm();
  isFormOpen.value = true;
}

// Open "how to" dialog
function openHowTo() {
  isHowToOpen.value = true;
}

// Fetch liveries by selected carname (store_name)
async function getLiveriesByCar(carname) {
  isLoading.value = true
  const [res] = await Promise.all([
    proxy.$api({ serviceName: "liveries", methodName: "getLiveriesByCar", parameters: [carname] }),
    new Promise(resolve => setTimeout(resolve, 800)) // Minimum 800ms loading
  ])
  liveries.value = res
  isLoading.value = false
}

// Apply the car filter and fetch filtered liveries from the API
function CarFilter(option) {
  if (option) {
    carfiltre.value = option.store_name;
    getLiveriesByCar(carfiltre.value);
  } else {
    carfiltre.value = '';
    getLiveries();
  }
}

function openEditForm(livery) {
  resetForm();
  if (livery.personaId === driver.persona.ID) {
    form.value.liveryName = livery.liveryname
    form.value.liveryCode = livery.code  // Préremplir le code de la livrée
    isEditMode.value = true  // Mode édition
    isFormOpen.value = true
  } else {
    console.log('Vous ne pouvez pas éditer cette livrée.')
  }
}

function handleImageSelected(event) {
  const file = event.target.files[0];

  if (file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      previewImage.value = e.target.result;

      const img = new Image();
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        const ratio = (width / height).toFixed(2);
        const expectedRatio = (16 / 9).toFixed(2);

        if (ratio !== expectedRatio) {
          errorMessage.value = "Please select an image with a 16:9 ratio."
          previewImage.value = null;
        } else {
          errorMessage.value = '';
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
    form.value.liveryImage = file;
  } else {
    previewImage.value = null;
  }
}

function toggleSortOrder() {
  sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc';
}

// Computed property for sorted liveries
const sortedLiveries = computed(() => {
  return [...liveries.value].sort((a, b) => {
    if (sortOrder.value === 'asc') {
      return a.useCount - b.useCount;
    } else {
      return b.useCount - a.useCount;
    }
  });
});

function resetForm() {
  form.value = {
    liveryName: '',
    liveryCode: '',
    liveryImage: null,
  };
  previewImage.value = null;
  errorMessage.value = '';
  isEditMode.value = false;
}

function uploadLivery(e) {
  e.preventDefault();

  if (!form.value.liveryName || !form.value.liveryCode) {
    errorMessage.value = 'Veuillez fournir toutes les informations requises.';
    return;
  }

  // En mode création, l'image est obligatoire
  if (!isEditMode.value && !form.value.liveryImage) {
    errorMessage.value = 'Veuillez sélectionner une image pour la nouvelle livrée.';
    return;
  }

  const fd = new FormData();

  // Ajouter l'image uniquement si elle est présente (non obligatoire en mode édition)
  if (form.value.liveryImage) {
    fd.append("file", form.value.liveryImage);
  }

  fd.append('easharpptr-u', localStorage.getItem('easharpptr_u'));
  fd.append('easharpptr-p', localStorage.getItem('easharpptr_p'));
  fd.append('livery_code', form.value.liveryCode);
  fd.append('livery_name', form.value.liveryName);

  const url = isEditMode.value 
    ? 'https://api.nightriderz.world/update_livery.php'
    : 'https://api.nightriderz.world/upload_livery.php';

  var XHR = new XMLHttpRequest();
  XHR.onreadystatechange = function () {
    if (this.readyState == 4) {
      if (this.status == 200) {
        const response = JSON.parse(this.responseText);
        if (response.success) {
          getLiveries();  // Recharge les livrées après succès
          isFormOpen.value = false;
          previewImage.value = null;
        } else {
          errorMessage.value = response.message;
        }
      } else {
        errorMessage.value = 'Erreur lors de l\'envoi de la livrée.';
      }
    }
  };

  XHR.open('POST', url);
  XHR.send(fd);
}

function openFile() {
  document.querySelector('.file').click();
}

// Initial call to load liveries and car list
getLiveries();
getCarClasses();
</script>

<template>
  <v-app-bar :elevation="2" class="modern-app-bar" style="overflow: initial;">
    <v-icon class="mr-3">mdi-palette</v-icon>
    <div class="app-bar-content">
      <v-app-bar-title class="app-bar-main-title">Liveries</v-app-bar-title>
      <div class="app-bar-subtitle">Browse and share custom car designs</div>
    </div>
    <v-spacer></v-spacer>
    <v-select 
      placeholder="Choose a car to filter results" 
      class="search" 
      density="compact" 
      solo 
      label="full_name" 
      :options="carList" 
      item-text="full_name" 
      item-value="store_name" 
      style="height: 32px;width: 420px;" 
      @update:modelValue="CarFilter"
    >
      <template v-slot:option="option">
        <div class="d-flex" style="justify-content: center;align-items: center;">
          <span style="width: 340px;text-overflow: ellipsis;overflow: hidden;text-transform: uppercase;">{{ option.full_name }}</span>
          <v-spacer></v-spacer>
          <v-chip size="small">{{ number_format(option.livery_count) }}</v-chip>
        </div>
      </template>
    </v-select>

    <v-btn size="small" @click="toggleSortOrder" style="background: #1f2430;border-radius: 4px;width: 32px; height: 32px; margin-left: 8px; min-width: 32px">
      <v-icon style="font-size: 20px;">{{ sortOrder === 'desc' ? 'mdi-sort-descending' : 'mdi-sort-ascending' }}</v-icon>
      <v-tooltip activator="parent" location="bottom">Invert sort order</v-tooltip>
    </v-btn>
    <!-- Publish Button -->
    <v-btn size="small" style="background: #1f2430; border-radius: 4px; width: 32px; height: 32px; margin-left: 8px; min-width: 32px" @click="publishLivery">
      <v-icon style="font-size: 20px;">mdi-publish</v-icon>
      <v-tooltip activator="parent" location="bottom">Publish a livery</v-tooltip>
    </v-btn>

    <!-- How to Button -->
    <v-btn size="small" style="background: #1f2430;border-radius: 4px;width: 32px; height: 32px; margin-left: 8px; min-width: 32px" @click="openHowTo">
      <v-icon style="font-size: 20px;">mdi-help-circle-outline</v-icon>
      <v-tooltip activator="parent" location="bottom">How to use</v-tooltip>
    </v-btn>
  </v-app-bar>

  <!-- Publish Livery Dialog -->
  <v-dialog v-model="isFormOpen" max-width="600px" scrollable>
    <v-card>
      <v-toolbar dark color="primary" class="livery-toolbar">
        <v-icon class="mr-2">mdi-palette</v-icon>
        <div class="toolbar-content">
          <v-toolbar-title class="toolbar-main-title">{{ isEditMode ? 'Edit a livery' : 'Publish a livery' }}</v-toolbar-title>
          <div class="toolbar-subtitle">{{ isEditMode ? 'Update your livery details' : 'Share your custom design with the community' }}</div>
        </div>
      </v-toolbar>
      <v-card-text>
        <v-form @submit.prevent="uploadLivery">
          <v-alert v-if="errorMessage" type="error">{{ errorMessage }}</v-alert>
          <v-text-field v-model="form.liveryName" label="Livery name" variant="outlined" required prepend-icon="mdi-tag-text"></v-text-field>
          <v-text-field v-model="form.liveryCode" label="Livery code" required variant="outlined" prepend-icon="mdi-qrcode" @input="form.liveryCode = form.liveryCode.toUpperCase()" :rules="[v => /^[A-Z0-9]{3}-[A-Z0-9]{3}-[A-Z0-9]{3}$/.test(v) || 'The code must be in the format ABC-123-DEF']" :disabled="isEditMode"></v-text-field>
          <input type="file" id="fileupload" class="file" @change="handleImageSelected" hidden>
          <img v-if="previewImage" :src="previewImage" alt="Image Preview" style="border-radius: 4px !important;position: fixed!important;width: 100px;top: 4px;right: 4px;"></img>
          <div class="d-flex justify-end">
            <v-btn @click="openFile" variant="tonal">Select an Image</v-btn>
            <v-btn type="submit" style="margin-left: 6px;background: rgb(38, 107, 173);">{{ isEditMode ? 'Save Changes' : 'Publish' }}</v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>


  <!-- How to Dialog -->
  <v-dialog v-model="isHowToOpen" max-width="550px" scrollable>
    <v-card>
      <v-toolbar dark color="primary" class="howto-toolbar">
        <v-icon class="mr-2">mdi-help-circle-outline</v-icon>
        <div class="toolbar-content">
          <v-toolbar-title class="toolbar-main-title">Livery System Usage</v-toolbar-title>
          <div class="toolbar-subtitle">Learn how to export and publish liveries</div>
        </div>
      </v-toolbar>
      <v-card-text>
        <h3>How to export and publish a livery?</h3>
        <ol>
          <li>In game, pick the car that has the livery you want to export.</li>
          <li>Open the chat and type <span style="background: black;font-size:14px;font-weight: 600;border-radius: 4px;"> /livery export </span> to get the <strong>code</strong>.</li>
          <li>Click on the <strong>upload</strong> button that is next to the car filter.</li>
          <li>Fill the <strong>form</strong> as it is indicated.</li>
          <li>Select an image of <strong>16:9 aspect ratio</strong> of your livery.</li>
          <li>Once all details are filled, click <strong>Publish</strong> to publish the livery.</li>
          <br>
          <span>Only the owner of a code can publish the livery linked to it.</span>
          <br>
          <span>The ideal image resolution is <span style="background: black;font-size:14px;font-weight: 600;border-radius: 4px;"> 600 x 338 </span>.</span>
        </ol>
        <br>
        <h3>How to install a livery?</h3>
        <ol>
          <li>In game, pick the car that you want to equip of a livery.</li>
          <li>Find the livery <strong>code</strong> on the website.</li>
          <li>Open the chat and execute the command <span style="background: black;font-size:14px;font-weight: 600;border-radius: 4px;"> /livery import 123-ABC-456 </span> to install the chosen <strong>livery</strong>.</li>
          <li>Leave or go to <strong>safehouse</strong> in order to refresh the car look.</li>
          <br>
          <span>In order to install a <strong>livery</strong> on another car model than the one it is made for, use the command suffix <span style="background: black;font-size:14px;font-weight: 600;border-radius: 4px;"> --force </span>.</span>
        </ol>
      </v-card-text>
      <v-card-actions>
        <v-btn color="primary" @click="isHowToOpen = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="isImagePopupOpen" min-width="300px" max-width="800px" style="border-radius:8px">
  <v-card style="border-radius:8px">
    <v-img :src="selectedLivery.imageUrl" :style="imageStyles" style="border-radius:8px"></v-img>
    <div class="d-flex" style="justify-content: center;align-items: center;margin-left: 8px;">
      <v-card class="card" style="text-align: center; position: absolute; margin-left: auto; margin-right: auto; padding: 0 6px 0px 6px; bottom: -15px;">
        <span><b>{{ selectedLivery.title }}</b> by <b>{{ selectedLivery.author }}</b></span>
      </v-card>
      <!--<v-card class="card" style="text-align: center;position:absolute; margin-left: auto; margin-right: auto;margin-top: -40px; padding: 0 6px 0px 6px">
        <span>{{ selectedLivery.code }}</span>
      </v-card>
      <v-card class="card" style="text-align: center;position:absolute; margin-left: auto; margin-right: auto;margin-top: -40px; padding: 0 6px 0px 6px">
        <span>{{ selectedLivery.personaName }}</span>
      </v-card>
      <v-card class="card" style="text-align: center;position:absolute; margin-left: auto; margin-right: auto;margin-top: -40px; padding: 0 6px 0px 6px">
        <span>{{ selectedLivery.car }}</span>
      </v-card>-->
    </div>
  </v-card>
</v-dialog>

  <!-- Display of filtered liveries -->
  <transition name="fade" mode="out-in">
    <!-- Skeleton loading state -->
    <div key="1" class="grid" v-if="isLoading">
      <SKliveryCard v-for="i in 12" :key="i" />
    </div>

    <!-- Actual liveries -->
    <div key="2" class="grid" v-else>
      <v-card v-for="(livery, index) in sortedLiveries" :key="index" class="livery-card" width="310">
        <!-- Download count badge -->
        <div class="download-badge">
          <v-icon size="18" class="mr-1">mdi-download</v-icon>
          <span>{{ livery.useCount }}</span>
        </div>

        <!-- Edit button (only for owner) -->
        <v-btn 
          v-if="livery.personaId === driver.persona.ID" 
          @click="openEditForm(livery)" 
          icon="mdi-pencil"
          size="small"
          class="edit-btn"
          variant="elevated"
        />

        <!-- Livery image -->
        <div class="livery-image-wrapper" @click="openImagePopup(livery)">
          <img 
            :src="'https://cdn.nightriderz.world/images/website/liveries/' + livery.code + '.png'" 
            class="livery-image" 
            :alt="livery.liveryname"
          />
          <div class="image-overlay">
            <v-icon size="48" color="white">mdi-magnify-plus</v-icon>
          </div>
        </div>

        <!-- Livery info -->
        <div class="livery-info">
          <div class="info-item">
            <v-icon size="20" class="info-icon">mdi-palette</v-icon>
            <span class="info-text livery-name">{{ livery.liveryname }}</span>
          </div>

          <div class="info-item">
            <v-icon size="20" class="info-icon">mdi-car</v-icon>
            <span class="info-text">{{ livery.carFullName }}</span>
          </div>

          <div class="info-item code-item">
            <v-icon size="20" class="info-icon">mdi-qrcode</v-icon>
            <span class="info-text code-text">{{ livery.code }}</span>
            <v-tooltip activator="parent" location="bottom">
              <div class="tooltip-content">
                Type in the game chat:<br>
                <code>/livery import {{ livery.code }}</code>
              </div>
            </v-tooltip>
          </div>

          <div class="info-item">
            <v-icon size="20" class="info-icon">mdi-racing-helmet</v-icon>
            <span class="info-text">{{ livery.personaName }}</span>
          </div>
        </div>
      </v-card>
    </div>
  </transition>
</template>

<style scoped>
.grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 20px;
  max-width: 3400px;
  margin: 0 auto;
}

/* Fade transition for skeleton/content switch */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Modern Livery Card */
.livery-card {
  background: var(--primary-color);
  margin: 25px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.livery-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  border-color: rgba(var(--v-theme-primary), 0.3);
}

/* Download badge */
.download-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
  background: rgba(17, 20, 28, 0.95);
  backdrop-filter: blur(8px);
  padding: 6px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

/* Edit button */
.edit-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  background: rgba(17, 20, 28, 0.95) !important;
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

/* Livery image */
.livery-image-wrapper {
  position: relative;
  width: calc(100% - 12px);
  margin: 6px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 16 / 9;
}

.livery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.livery-card:hover .livery-image {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.livery-image-wrapper:hover .image-overlay {
  opacity: 1;
}

/* Livery info section */
.livery-info {
  padding: 0 6px 6px 6px;
}

.info-item {
  display: flex;
  align-items: center;
  background: rgba(31, 36, 48, 0.8);
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 6px;
  gap: 12px;
  transition: background 0.2s ease;
  position: relative;
}

.info-item:hover {
  background: rgba(31, 36, 48, 1);
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-icon {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.7);
}

.info-text {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.livery-name {
  text-transform: uppercase;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
}

.code-item {
  cursor: help;
}

.code-text {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  letter-spacing: 1px;
}

.tooltip-content code {
  background: rgba(0, 0, 0, 0.5);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
}

/* Old styles to keep */
.v-card-subtitle {
  background: var(--primary-color);
  display: flex;
  justify-content: center;
  padding: 10px 0;
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 6px;
  opacity: 1;
}

.v-select::v-deep(li) {
  margin: 0;
  overflow: hidden;
}

.v-select::v-deep(v-chip) {
  margin: 0;
  padding: 0;
}

.v-select::v-deep(button) {
  background: transparent;
}

.search {
  width: 450px;
  background: rgb(var(--v-theme-surface));
  border-radius: 4px;
  padding-right: 3px;
}

.v-img__img.v-img__img--contain {
  position: fixed !important;
}

/* Toolbar Styles */
.livery-toolbar,
.howto-toolbar {
  padding: 8px 16px !important;
}

.toolbar-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.toolbar-main-title {
  font-size: 1.25rem !important;
  font-weight: 600 !important;
  line-height: 1.2 !important;
}

.toolbar-subtitle {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1;
}

/* Modern App Bar Styles */
.modern-app-bar {
  padding: 8px 16px !important;
}

.app-bar-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  margin-right: 16px;
}

.app-bar-main-title {
  font-size: 1.4rem !important;
  font-weight: 600 !important;
  line-height: 1.2 !important;
}

.app-bar-subtitle {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1;
}
</style>
